import React, { useState } from "react";
import {
  FaAngleDown,
  FaAngleUp,
  FaChair,
  FaClock,
  FaLuggageCart,
  FaPlane,
  FaSuitcaseRolling,
} from "react-icons/fa";
import { GiKnifeFork } from "react-icons/gi";
import { serviceFeeOf } from "../../utils/pricing";
import {
  computeDurationMinutes,
  formatLocalDateWithAdjustment,
  getTimezoneAbbreviation,
} from "../../utils/dateUtils";

export interface FlightDetailsAccordionTier {
  label?: string;
  baggage?: string;
  fareBasisCode?: string;
  fareType?: string;
  fullGroup?: any;
  refundPolicy?: {
    refundable: boolean;
    cancellationFee?: { amount: number; currency: string };
    conditions?: string;
  };
  changePolicy?: {
    changeable: boolean;
    changeFee?: { amount: number; currency: string; type: string };
  };
}

interface FlightDetailsAccordionProps {
  flightData: any;
  tier?: FlightDetailsAccordionTier | null;
  baseDepartureDate?: string;
  fallbackBaggage?: string;
  fallbackCurrency?: string;
  fallbackBaseFare?: number;
  fallbackTaxes?: number;
  fallbackTotal?: number;
}

const cabinClassOptions = [
  { label: "Economy", code: "Y" },
  { label: "Premium Economy", code: "S" },
  { label: "Business", code: "C" },
  { label: "Premium Business", code: "J" },
  { label: "First Class", code: "F" },
  { label: "Premium First", code: "P" },
];

function getCabinLabel(code?: string) {
  return cabinClassOptions.find((c) => c.code === code)?.label || code || "Unknown";
}

function extractTime(dateTimeStr: string): string {
  if (!dateTimeStr) return "--:--";
  const match = dateTimeStr.match(/(\d{2}:\d{2})/);
  return match ? match[1] : dateTimeStr;
}

function computeLayoverMinutes(
  arrStr: string,
  arrDayAdj: number,
  depStr: string,
  depDayAdj: number,
): number {
  if (!arrStr || !depStr) return 0;
  const arrD = new Date(arrStr);
  const depD = new Date(depStr);
  if (!isNaN(arrD.getTime()) && !isNaN(depD.getTime())) {
    return Math.max(0, Math.round((depD.getTime() - arrD.getTime()) / 60000));
  }
  const toMins = (t: string, dayAdj: number): number | null => {
    const m = t.match(/(\d{2}):(\d{2})(?::\d{2})?([+-])(\d{2}):(\d{2})/);
    if (!m) return null;
    const local = parseInt(m[1]) * 60 + parseInt(m[2]);
    const sign = m[3] === "+" ? 1 : -1;
    const off = sign * (parseInt(m[4]) * 60 + parseInt(m[5]));
    return local - off + dayAdj * 1440;
  };
  const arr = toMins(arrStr, arrDayAdj);
  const dep = toMins(depStr, depDayAdj);
  if (arr === null || dep === null) return 0;
  return Math.max(0, dep - arr);
}

function formatElapsedTime(minutes?: number): string {
  if (!minutes || isNaN(minutes)) return "-";
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function getAircraftName(code?: string): string {
  const map: Record<string, string> = {
    "32A": "Airbus A320", "320": "Airbus A320", "321": "Airbus A321",
    "332": "Airbus A330-200", "333": "Airbus A330-300", "330": "Airbus A330",
    "359": "Airbus A350-900", "388": "Airbus A380-800", "380": "Airbus A380",
    "789": "Boeing 787-9", "788": "Boeing 787-8", "77W": "Boeing 777-300ER",
    "772": "Boeing 777-200", "744": "Boeing 747-400", "73H": "Boeing 737-800",
  };
  return map[code ?? ""] || (code ? `Aircraft (${code})` : "Aircraft");
}

function getAirlineName(code?: string): string {
  const map: Record<string, string> = {
    EY: "Etihad Airways", EK: "Emirates", FZ: "Flydubai",
    OZ: "Asiana Airlines", KE: "Korean Air", QR: "Qatar Airways",
    AI: "Air India", SQ: "Singapore Airlines", TK: "Turkish Airlines",
  };
  return map[code ?? ""] || code || "Airline";
}

const FlightDetailsAccordion: React.FC<FlightDetailsAccordionProps> = ({
  flightData,
  tier,
  baseDepartureDate,
  fallbackBaggage,
  fallbackCurrency,
  fallbackBaseFare,
  fallbackTaxes,
  fallbackTotal,
}) => {
  const [showFlightDetails, setShowFlightDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "flight" | "fare" | "cancellation" | "datechange"
  >("flight");

  const buildFlightSegments = () => {
    const legs = flightData?.legs || [];
    const result: any[] = [];
    legs.forEach((leg: any, legIndex: number) => {
      const segs = leg.segments || [];
      segs.forEach((segment: any, segIndex: number) => {
        result.push({
          ...segment,
          legIndex,
          segmentIndex: segIndex,
          isLastInLeg: segIndex === segs.length - 1,
          totalLegStops: leg.stops ?? segs.length - 1,
          legDuration: leg.duration,
          legDirection: leg.direction,
          legDeparture: leg.departure,
          legArrival: leg.arrival,
        });
      });
    });
    for (let i = 0; i < result.length - 1; i++) {
      const curr = result[i];
      const next = result[i + 1];
      result[i].layoverToNext = computeLayoverMinutes(
        curr.arrival?.dateTime ?? curr.arrival?.time ?? "",
        curr.arrival?.dateAdjustment ?? 0,
        next.departure?.dateTime ?? next.departure?.time ?? "",
        next.departure?.dateAdjustment ?? 0,
      );
    }
    return result;
  };

  const getPricingBreakdown = () => {
    // One derivation for every branch, shared with the card headline and the
    // booking popup. Each branch used to read the fee from its own place, so a
    // fare whose group carried a price but no markup showed a zero fee here
    // while the card beside it showed the itinerary's.
    const serviceFee = serviceFeeOf(flightData, tier);

    if (tier?.fullGroup?.price) {
      return {
        baseFare: tier.fullGroup.price.baseFare || 0,
        taxes: tier.fullGroup.price.taxes || 0,
        total: tier.fullGroup.price.total || 0,
        currency: tier.fullGroup.price.currency || fallbackCurrency || "",
        serviceFee,
      };
    }
    if (flightData?.pricingInfo) {
      return {
        baseFare:
          flightData.pricingInfo.airlineBaseFare ||
          flightData.pricingInfo.baseFare ||
          0,
        taxes: flightData.pricingInfo.taxes || 0,
        total:
          flightData.pricingInfo.total ||
          flightData.pricingInfo.amount ||
          0,
        currency: flightData.pricingInfo.currency || fallbackCurrency || "",
        serviceFee,
      };
    }
    return {
      baseFare: fallbackBaseFare || 0,
      taxes: fallbackTaxes || 0,
      total: fallbackTotal || 0,
      currency: fallbackCurrency || "",
      serviceFee,
    };
  };

  const pricing = getPricingBreakdown();

  const payableTotal = pricing.total;
  const displayedBaggage = tier?.baggage || fallbackBaggage || "";

  const renderFlightDetails = () => {
    const flightSegments = buildFlightSegments();
    if (flightSegments.length === 0) {
      return (
        <div className="text-center py-6 text-gray-500">
          No flight details available.
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-100">
        {flightSegments.map((segment, idx) => {
          const showLegHeader =
            idx === 0 || segment.legIndex !== flightSegments[idx - 1].legIndex;
          const marketingCode = segment.marketingCarrier?.code ?? "";
          const operatingCode = segment.operatingCarrier?.code ?? "";
          const marketingNum = segment.marketingCarrier?.flightNumber ?? "";
          const operatingNum = segment.operatingCarrier?.flightNumber ?? "";
          const isCodeshare = operatingCode && operatingCode !== marketingCode;

          const depAirport = segment.departure?.airport?.code ?? "-";
          const arrAirport = segment.arrival?.airport?.code ?? "-";
          const depTerminal = segment.departure?.airport?.terminal ?? "—";
          const arrTerminal = segment.arrival?.airport?.terminal ?? "—";
          const depTimeStr = extractTime(
            segment.departure?.dateTime ?? segment.departure?.time ?? "",
          );
          const arrTimeStr = extractTime(
            segment.arrival?.dateTime ?? segment.arrival?.time ?? "",
          );
          const isArrNextDay = (segment.arrival?.dateAdjustment ?? 0) > 0;

          const seats =
            segment.bookingInfo?.seatsAvailable ?? segment.seatsAvailable;
          const meal =
            segment.bookingInfo?.meal?.description ??
            (segment.bookingInfo?.meal?.code === "M" ? "Meal included" : null);
          const cabinCode = segment.bookingInfo?.cabinCode ?? segment.cabinCode;
          const bookingClass =
            segment.bookingInfo?.bookingClass ?? segment.bookingClass;

          const legBase =
            segment.legDeparture?.dateTime ?? baseDepartureDate;
          const depDate = legBase
            ? formatLocalDateWithAdjustment(legBase, 0).date
            : "";
          const arrDate = legBase
            ? formatLocalDateWithAdjustment(
                legBase,
                segment.arrival?.dateAdjustment ?? 0,
              ).date
            : "";

          const layoverMins: number = segment.layoverToNext ?? 0;
          const isLastSegmentOfLeg = segment.isLastInLeg;
          const isLastSegmentOverall = idx === flightSegments.length - 1;
          const showLayover =
            !isLastSegmentOverall && !isLastSegmentOfLeg && layoverMins > 0;

          return (
            <div key={`seg-${idx}`}>
              {showLegHeader && flightData?.legs?.length > 1 && (
                <div className="px-1 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  {segment.legDirection
                    ? segment.legDirection === "outbound"
                      ? "Outbound"
                      : segment.legDirection === "inbound"
                      ? "Return"
                      : `Leg ${segment.legIndex + 1}`
                    : `Leg ${segment.legIndex + 1}`}
                  {segment.legDeparture?.airport?.code &&
                    segment.legArrival?.airport?.code && (
                      <span className="ml-2 text-gray-500 normal-case font-normal">
                        {segment.legDeparture.airport.code} →{" "}
                        {segment.legArrival.airport.code}
                      </span>
                    )}
                </div>
              )}
              <div className="py-4 px-1">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={`https://imgak.mmtcdn.com/flights/assets/media/dt/common/icons/${marketingCode}.png`}
                    alt={marketingCode}
                    className="w-9 h-9 object-contain rounded"
                    onError={(e) =>
                      ((e.target as HTMLImageElement).src = `https://via.placeholder.com/36?text=${marketingCode}`)
                    }
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-gray-800">
                      {getAirlineName(marketingCode)} · {marketingCode}{" "}
                      {marketingNum}
                    </div>
                    {isCodeshare && (
                      <div className="text-xs text-gray-400">
                        Operated by {getAirlineName(operatingCode)} (
                        {operatingCode} {operatingNum})
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    {getAircraftName(segment.equipment?.code)}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="min-w-[80px]">
                    <div className="text-2xl font-semibold text-gray-900">
                      {depTimeStr}
                      {(() => {
                        const tz = getTimezoneAbbreviation(
                          segment.departure?.dateTime ?? segment.departure?.time,
                        );
                        return tz ? (
                          <span className="ml-1 text-[10px] font-normal text-gray-400 align-top">
                            {tz}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-sm font-semibold text-gray-700">
                      {depAirport}
                    </div>
                    {depDate && (
                      <div className="text-xs text-gray-500">{depDate}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-0.5">
                      Terminal {depTerminal}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col items-center gap-1 px-2">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <FaClock size={10} />
                      {formatElapsedTime(
                        segment.duration ||
                          computeDurationMinutes(
                            segment.departure?.dateTime ?? segment.departure?.time,
                            segment.arrival?.dateTime ?? segment.arrival?.time,
                            segment.arrival?.dateAdjustment ?? 0,
                          ),
                      )}
                    </div>
                    <div className="w-full flex items-center gap-1">
                      <div className="flex-1 border-t border-dashed border-gray-300" />
                      <FaPlane size={12} className="text-primary shrink-0" />
                      <div className="flex-1 border-t border-dashed border-gray-300" />
                    </div>
                    <div className="text-xs text-gray-400">Non-stop segment</div>
                  </div>

                  <div className="min-w-[80px] text-right">
                    <div className="text-2xl font-semibold text-gray-900">
                      {arrTimeStr}
                      {isArrNextDay && (
                        <span className="ml-1 text-[11px] font-normal text-orange-500 align-super">
                          +1
                        </span>
                      )}
                      {(() => {
                        const tz = getTimezoneAbbreviation(
                          segment.arrival?.dateTime ?? segment.arrival?.time,
                        );
                        return tz ? (
                          <span className="ml-1 text-[10px] font-normal text-gray-400 align-top">
                            {tz}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-sm font-semibold text-gray-700">
                      {arrAirport}
                    </div>
                    {arrDate && (
                      <div className="text-xs text-gray-500">{arrDate}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-0.5">
                      Terminal {arrTerminal}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {cabinCode && (
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                      <FaChair size={9} /> {getCabinLabel(cabinCode)}
                      {bookingClass && ` (${bookingClass})`}
                    </span>
                  )}
                  {seats !== undefined && (
                    <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">
                      {seats} seat{seats === 1 ? "" : "s"} left
                    </span>
                  )}
                  {meal && (
                    <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                      <GiKnifeFork size={9} /> {meal}
                    </span>
                  )}
                </div>
              </div>

              {showLayover && (
                <div className="mx-1 my-1 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                  <FaClock size={12} className="shrink-0" />
                  <span>
                    <span className="font-semibold">
                      {formatElapsedTime(layoverMins)} layover
                    </span>{" "}
                    · Change of planes at{" "}
                    <span className="font-semibold">
                      {segment.arrival?.airport?.code ?? "—"}
                    </span>{" "}
                    — {segment.arrival?.airport?.city ?? ""}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFareSummary = () => (
    <div>
      <div className="space-y-1 mb-4">
        <div className="flex justify-between py-2.5 border-b border-gray-100">
          <span className="text-gray-600 text-sm">Base Fare (1 Adult)</span>
          <span className="font-semibold text-sm">
            {pricing.currency} {pricing.baseFare.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between py-2.5 border-b border-gray-100">
          <span className="text-gray-600 text-sm">Taxes &amp; Fees</span>
          <span className="font-semibold text-sm">
            {pricing.currency} {pricing.taxes.toLocaleString()}
          </span>
        </div>
        {pricing.serviceFee > 0 && (
          <div className="flex justify-between py-2.5 border-b border-gray-100">
            <span className="text-gray-600 text-sm">Service Fee</span>
            <span className="font-semibold text-sm text-primary">
              {pricing.currency} {pricing.serviceFee.toLocaleString()}
            </span>
          </div>
        )}

        <div className="flex justify-between py-3">
          <span className="font-bold text-base text-gray-900">Total</span>
          <span className="font-bold text-base text-primary">
            {pricing.currency} {payableTotal.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 space-y-2 mb-3">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Baggage Allowance
        </div>
        {tier?.fullGroup?.baggage?.cabin && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FaLuggageCart size={12} className="text-gray-400" />
            <span>Cabin: {tier.fullGroup.baggage.cabin.description}</span>
          </div>
        )}
        {tier?.fullGroup?.baggage?.checked && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FaSuitcaseRolling size={12} className="text-gray-400" />
            <span>Checked: {tier.fullGroup.baggage.checked.description}</span>
          </div>
        )}
        {!tier?.fullGroup?.baggage && displayedBaggage && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FaLuggageCart size={12} className="text-gray-400" />
            <span>{displayedBaggage}</span>
          </div>
        )}
      </div>

      {tier?.fareBasisCode && (
        <div className="text-xs text-gray-400 pt-1">
          Fare basis: <span className="font-mono">{tier.fareBasisCode}</span>
          {tier.fareType && ` · Type: ${tier.fareType}`}
        </div>
      )}
    </div>
  );

  const renderCancellation = () => {
    const policy = tier?.refundPolicy ?? tier?.fullGroup?.refundPolicy;
    if (!policy)
      return (
        <div className="text-gray-500 text-sm py-2">
          Cancellation policy not available.
        </div>
      );

    return (
      <div className="space-y-3">
        <div
          className={`flex items-start gap-3 p-4 rounded-xl border ${
            policy.refundable
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          }`}
        >
          <div
            className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs shrink-0 ${
              policy.refundable ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {policy.refundable ? "✓" : "✕"}
          </div>
          <div>
            <div
              className={`font-semibold text-sm ${
                policy.refundable ? "text-green-800" : "text-red-800"
              }`}
            >
              {policy.refundable
                ? "Refundable Ticket"
                : "Non-refundable Ticket"}
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {policy.refundable
                ? "Cancellation is allowed. Refund will be credited to the original payment method as per the airline policy."
                : policy.cancellationFee
                ? `A cancellation fee of ${policy.cancellationFee.amount} ${policy.cancellationFee.currency} applies. Taxes may be partially refundable.`
                : "No refund will be issued upon cancellation. Applicable taxes may be refunded — contact the airline directly."}
            </p>
          </div>
        </div>

        {policy.conditions && (
          <div className="text-sm text-gray-500 px-1">
            <span className="font-semibold text-gray-600">Conditions: </span>
            {policy.conditions}
          </div>
        )}

        <div className="text-xs text-gray-400 px-1 pt-1">
          Fare: <span className="font-semibold">{tier?.label}</span>
          {tier?.fareBasisCode && (
            <>
              {" "}· Basis: <span className="font-mono">{tier.fareBasisCode}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDateChange = () => {
    const policy = tier?.changePolicy ?? tier?.fullGroup?.changePolicy;
    if (!policy)
      return (
        <div className="text-gray-500 text-sm py-2">
          Change policy not available.
        </div>
      );

    return (
      <div className="space-y-3">
        <div
          className={`flex items-start gap-3 p-4 rounded-xl border ${
            policy.changeable
              ? "bg-blue-50 border-blue-200"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div
            className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs shrink-0 ${
              policy.changeable ? "bg-blue-500" : "bg-gray-400"
            }`}
          >
            {policy.changeable ? "✓" : "✕"}
          </div>
          <div>
            <div
              className={`font-semibold text-sm ${
                policy.changeable ? "text-blue-800" : "text-gray-700"
              }`}
            >
              {policy.changeable
                ? "Date Change Allowed"
                : "Non-changeable Ticket"}
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {policy.changeable
                ? policy.changeFee
                  ? `A change fee of ${policy.changeFee.amount} ${policy.changeFee.currency} applies per change, plus any fare difference on the new itinerary.`
                  : "Date changes are permitted free of charge. Fare difference may apply."
                : "No date changes are permitted after booking on this fare."}
            </p>
          </div>
        </div>

        {policy.changeable && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Change fee</span>
              <span className="font-semibold text-gray-800">
                {policy.changeFee
                  ? `${policy.changeFee.currency} ${policy.changeFee.amount.toLocaleString()}`
                  : "Free"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Fare difference</span>
              <span className="font-semibold text-gray-800">
                Applicable if higher
              </span>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-400 px-1 pt-1">
          Fare: <span className="font-semibold">{tier?.label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowFlightDetails((prev) => !prev);
        }}
        className="w-full flex items-center gap-2 px-4 sm:px-6 py-3 text-primary font-medium text-sm hover:bg-gray-100 transition-colors focus:outline-none"
      >
        {showFlightDetails ? <FaAngleUp size={14} /> : <FaAngleDown size={14} />}
        {showFlightDetails ? "Hide Flight Details" : "View Flight Details"}
      </button>

      {showFlightDetails && (
        <div className="border-t border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto bg-white px-4 sm:px-6">
            {(
              [
                { key: "flight", label: "Flight Details" },
                { key: "fare", label: "Fare Summary" },
                { key: "cancellation", label: "Cancellation" },
                { key: "datechange", label: "Date Change" },
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab(key);
                }}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTab === key
                    ? "text-primary border-primary"
                    : "text-gray-500 border-transparent hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="px-4 sm:px-6 py-4 bg-white">
            {activeTab === "flight" && renderFlightDetails()}
            {activeTab === "fare" && renderFareSummary()}
            {activeTab === "cancellation" && renderCancellation()}
            {activeTab === "datechange" && renderDateChange()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightDetailsAccordion;
